import { Fragment, useEffect, useRef, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XIcon } from '@heroicons/react/outline'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import { Button, Checkbox, ConfirmDialog } from '../../../components'
import { fetchInboundFranjaSummary, fetchReceivingCapacityOverrides } from '../../../services'
import { boardLocale } from '../../InboundAppointments/FranjaCapacityStrip'
import { isNewerOverride } from '../../../utils/inboundAppointments'

const T = 'replenishments.inbound_appointments.overrides.form'

// Mirrors the model/controller bound (receiving_capacity_override.rb,
// receiving_capacity_overrides_controller.rb): a franja's door-derived
// capacity runs in the thousands, so this is ~100x headroom while staying
// far inside the backend's int4 column. Without it, an exponent typo like
// 2e30 (meant as 2e3) passes Number.isInteger and would only be caught by
// a round-trip to the API's 400.
const UNITS_DELTA_BOUND = 1_000_000
// Shared by both preview fetches. Named rather than inlined twice, matching the
// FIVE_MINUTES_MS convention the sibling list container already set for this page.
const PREVIEW_QUERY_OPTIONS = { staleTime: 60 * 1000, refetchOnWindowFocus: false, retry: 1 }

// Append-only create modal (contract §4.5). Backend rule mirrored here:
// man_hours_override is day-scoped ONLY — combining it with a franja is a 422
// invalid_override, so the field is disabled while a franja is selected.
// dock, man-hours and units_delta are alternative levers: filling one clears
// the other two, so only one adjustment applies per row.
// The modal also fetches the franja summary and the date's own override rows
// to render an advisory capacity preview (see the derivation below).
// reason is required by the UI even though the API accepts its absence.
export const ReceivingCapacityOverrideFormModal = ({ open, setOpen, franjas = [], onSubmit, isLoading = false, serverError = null, warehouseId = null }) => {
  const { i18n } = useTranslation()
  const dateInputRef = useRef(null)

  const [date, setDate] = useState('')
  const [franjaId, setFranjaId] = useState(null)
  const [dockCount, setDockCount] = useState('')
  const [manHours, setManHours] = useState('')
  const [unitsDelta, setUnitsDelta] = useState('')
  const [blocked, setBlocked] = useState(false)
  const [reason, setReason] = useState('')
  const [confirmBlockedOpen, setConfirmBlockedOpen] = useState(false)

  useEffect(() => {
    if (!open) return
    setDate('')
    setFranjaId(null)
    setDockCount('')
    setManHours('')
    setUnitsDelta('')
    setBlocked(false)
    setReason('')
    setConfirmBlockedOpen(false)
  }, [open])

  const handleFranjaChange = value => {
    setFranjaId(value)
    if (value) setManHours('')
    else setUnitsDelta('') // units deltas are franja-scoped only
  }
  const handleDockChange = value => {
    setDockCount(value)
    if (value !== '') {
      setManHours('')
      setUnitsDelta('')
    }
  }
  const handleManHoursChange = value => {
    setManHours(value)
    if (value !== '') {
      setDockCount('')
      setUnitsDelta('')
    }
  }
  const handleUnitsDeltaChange = value => {
    setUnitsDelta(value)
    if (value !== '') {
      setDockCount('')
      setManHours('')
    }
  }

  // The min/step input attributes don't stop typed values: a negative or
  // fractional dock count would silently reach the API (parseInt truncates).
  const dockValid = dockCount === '' || (Number.isInteger(Number(dockCount)) && Number(dockCount) >= 0)
  const manHoursValid = manHours === '' || (!Number.isNaN(Number(manHours)) && Number(manHours) >= 0)
  // Negative is a legitimate delta (shrink volume); fractional is not — and
  // the franja requirement is belt-and-braces: the field is disabled and
  // cleared without one, but typed state must never outlive that invariant.
  // The bound catches an exponent typo (2e30 for 2e3) before it ever reaches
  // the API — Number.isInteger alone is true for such values.
  const unitsDeltaValid = unitsDelta === '' || (Number.isInteger(Number(unitsDelta)) && !!franjaId && Math.abs(Number(unitsDelta)) <= UNITS_DELTA_BOUND)
  const isValid =
    !!date && reason.trim().length > 0 && (blocked || dockCount !== '' || manHours !== '' || unitsDelta !== '') && dockValid && manHoursValid && unitsDeltaValid

  const buildPayload = () => ({
    date,
    blocked,
    reason: reason.trim(),
    ...(franjaId ? { receiving_franja_id: franjaId } : {}),
    ...(dockCount !== '' ? { dock_count_override: parseInt(dockCount, 10) } : {}),
    ...(manHours !== '' ? { man_hours_override: parseFloat(manHours) } : {}),
    // Number(), not parseInt(): validation uses Number semantics, and
    // parseInt silently truncates exponent input ('2e3' -> 2, not 2000).
    ...(unitsDelta !== '' ? { units_delta: Number(unitsDelta) } : {}),
  })

  const handleSubmit = e => {
    e.preventDefault()
    if (!isValid || isLoading) return
    // Blocked rows are append-only AND sticky — nothing in the v1 UI can undo
    // them, so this one-way door gets an explicit confirm (eng-review D19).
    if (blocked) {
      setConfirmBlockedOpen(true)
      return
    }
    onSubmit(buildPayload())
  }

  const handleClose = () => {
    if (isLoading) return
    setOpen(false)
  }

  // Franja summary for the picked date: shows the operator what today's
  // capacity is and what the typed delta produces. Purely advisory — any
  // fetch failure just hides the preview, never blocks the submit.
  // Gated on franjaId AND a typed delta: the preview can only render for a
  // franja-scoped row that carries one, so every other flow through this modal
  // (day-scoped levers, and franja-scoped dock or block-only rows) would fetch
  // twice and read neither.
  const { data: franjaSummary } = useQuery(['inbound_franja_summary', warehouseId, date], () => fetchInboundFranjaSummary(warehouseId, { date }), {
    enabled: open && !!warehouseId && !!date && !!franjaId && unitsDelta !== '',
    ...PREVIEW_QUERY_OPTIONS,
  })
  // The summary's unit_capacity already INCLUDES any existing delta, and
  // resolution is newest-wins REPLACE — so a truthful preview must recover
  // the base by subtracting the newest existing franja delta before adding
  // the typed one. Fetched date-scoped so the list page's picked range can
  // never hide the rows this arithmetic needs.
  const { data: dayOverrides } = useQuery(
    ['receiving_capacity_overrides', warehouseId, { from: date, to: date }],
    () => fetchReceivingCapacityOverrides(warehouseId, { from: date, to: date }),
    { enabled: open && !!warehouseId && !!date && !!franjaId && unitsDelta !== '', ...PREVIEW_QUERY_OPTIONS },
  )
  const previewRow = (franjaSummary || []).find(f => f.receiving_franja_id === franjaId)
  const existingDelta =
    (dayOverrides || [])
      .filter(o => o.receiving_franja_id === franjaId && o.units_delta != null)
      .reduce((newest, o) => (isNewerOverride(o, newest) ? o : newest), null)?.units_delta ?? 0
  // Same bound as unitsDeltaValid on purpose: previewing a confident number for a value
  // Crear will refuse reads as though the adjustment were valid.
  const parsedDelta = unitsDelta !== '' && Number.isInteger(Number(unitsDelta)) && Math.abs(Number(unitsDelta)) <= UNITS_DELTA_BOUND ? Number(unitsDelta) : null
  const locale = boardLocale(i18n)
  // The backend clamps resolution at 0 ([base + delta, 0].max, capacity.rb) —
  // a clamp that throws away information. If an existing NEGATIVE delta
  // already drove the resolved capacity to exactly 0 (e.g. base 6,000 +
  // delta -8,000 clamps to 0), subtracting that delta back does NOT recover
  // the true base: 0 - (-8,000) = 8,000 overstates it by 8,000. With only the
  // clamped unit_capacity and the override rows, the frontend cannot tell an
  // honest zero from a clamped one, so this hides the preview instead of
  // showing a confidently wrong number — the same "hide rather than mislead"
  // posture already used below for blocked franjas and fetch failures.
  const baseIsUnrecoverable = previewRow?.unit_capacity === 0 && existingDelta < 0
  const capacityPreview =
    previewRow && !previewRow.blocked && !baseIsUnrecoverable && parsedDelta != null && dayOverrides
      ? i18n.t(`${T}.capacity_preview`, {
          current: previewRow.unit_capacity.toLocaleString(locale),
          result: Math.max(previewRow.unit_capacity - existingDelta + parsedDelta, 0).toLocaleString(locale),
        })
      : null

  const inputClasses =
    'shadow-sm block w-full sm:text-sm rounded-md border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50 disabled:bg-gray-50'

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="fixed z-10 inset-0 overflow-y-auto" initialFocus={dateInputRef} onClose={handleClose}>
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
            &#8203;
          </span>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-visible shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="flex justify-between items-center mb-4">
                <Dialog.Title as="h3" className="text-lg leading-6 font-medium text-gray-900">
                  {i18n.t(`${T}.title`)}
                </Dialog.Title>
                <button type="button" className="text-gray-400 hover:text-gray-500" onClick={handleClose} aria-label={i18n.t(`${T}.cancel`)}>
                  <XIcon className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="override-date">
                    {i18n.t(`${T}.date`)}
                  </label>
                  <input id="override-date" ref={dateInputRef} type="date" className={inputClasses} value={date} onChange={e => setDate(e.target.value)} />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="override-franja">
                    {i18n.t(`${T}.franja`)}
                  </label>
                  <select
                    id="override-franja"
                    className={inputClasses}
                    value={franjaId ?? ''}
                    onChange={e => handleFranjaChange(e.target.value ? Number(e.target.value) : null)}
                  >
                    <option value="">{i18n.t('replenishments.inbound_appointments.overrides.all_day')}</option>
                    {franjas.map(franja => (
                      <option key={franja.id} value={franja.id}>
                        {`${franja.label} · ${franja.starts_at} – ${franja.ends_at}`}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="override-dock">
                      {i18n.t(`${T}.dock`)}
                    </label>
                    <input
                      id="override-dock"
                      type="number"
                      min="0"
                      step="1"
                      className={inputClasses}
                      value={dockCount}
                      onChange={e => handleDockChange(e.target.value)}
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="override-man-hours">
                      {i18n.t(`${T}.man_hours`)}
                    </label>
                    <input
                      id="override-man-hours"
                      type="number"
                      min="0"
                      step="0.5"
                      className={inputClasses}
                      value={manHours}
                      onChange={e => handleManHoursChange(e.target.value)}
                      disabled={!!franjaId}
                    />
                    {!!franjaId && <p className="mt-1 text-xs text-gray-500">{i18n.t(`${T}.man_hours_day_only`)}</p>}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="override-units-delta">
                    {i18n.t(`${T}.units_delta`)}
                  </label>
                  <input
                    id="override-units-delta"
                    type="number"
                    step="1"
                    className={inputClasses}
                    value={unitsDelta}
                    onChange={e => handleUnitsDeltaChange(e.target.value)}
                    disabled={!franjaId}
                  />
                  {!franjaId && <p className="mt-1 text-xs text-gray-500">{i18n.t(`${T}.units_delta_franja_only`)}</p>}
                  {capacityPreview && (
                    <p role="status" className="mt-1 text-xs text-gray-600">
                      {capacityPreview}
                    </p>
                  )}
                </div>

                <Checkbox label={i18n.t(`${T}.blocked`)} checked={blocked} onChange={isChecked => setBlocked(isChecked)} />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="override-reason">
                    {i18n.t(`${T}.reason`)}
                  </label>
                  <input id="override-reason" type="text" className={inputClasses} value={reason} onChange={e => setReason(e.target.value)} />
                </div>

                {serverError && <div className="font-bold text-red-500 text-sm">{serverError}</div>}

                <div className="flex justify-end gap-2 pt-2">
                  <Button type="secondary" onClick={handleClose} disabled={isLoading} className="px-4">
                    {i18n.t(`${T}.cancel`)}
                  </Button>
                  <Button type="primary" buttonType="submit" loading={isLoading} disabled={!isValid} className="px-4">
                    {i18n.t(`${T}.create`)}
                  </Button>
                </div>
              </form>

              <ConfirmDialog
                open={confirmBlockedOpen}
                setOpen={setConfirmBlockedOpen}
                title={i18n.t(`${T}.blocked_confirm_title`)}
                description={i18n.t(`${T}.blocked_confirm_description`)}
                confirmLabel={i18n.t(`${T}.blocked_confirm`)}
                cancelLabel={i18n.t(`${T}.cancel`)}
                onConfirm={() => {
                  setConfirmBlockedOpen(false)
                  onSubmit(buildPayload())
                }}
                onCancel={() => setConfirmBlockedOpen(false)}
                type="danger"
              />
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
